import { useMemo } from 'react';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ArrowDownRight, ArrowRight, ArrowUpRight } from 'lucide-react';
import type { PulseRow } from '@/lib/pulse/api';
import { cn } from '@/lib/utils';

interface Props {
  pulses: PulseRow[];
  days?: number;
}

interface Point {
  day: string; // ISO yyyy-mm-dd
  label: string; // short display
  value: number | null;
  isToday: boolean;
}

function buildSeries(pulses: PulseRow[], days: number): Point[] {
  const today = new Date();
  today.setUTCHours(0, 0, 0, 0);
  const byDay = new Map<string, number>();
  for (const p of pulses) byDay.set(p.created_day, p.value);

  const fmt = new Intl.DateTimeFormat(undefined, { weekday: 'short', day: 'numeric' });
  const out: Point[] = [];
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setUTCDate(d.getUTCDate() - i);
    const key = d.toISOString().slice(0, 10);
    out.push({
      day: key,
      label: fmt.format(d),
      value: byDay.has(key) ? byDay.get(key)! : null,
      isToday: i === 0,
    });
  }
  return out;
}

function average(values: number[]): number | null {
  if (!values.length) return null;
  return values.reduce((a, b) => a + b, 0) / values.length;
}

function TooltipCard({ active, payload }: { active?: boolean; payload?: Array<{ payload: Point }> }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  if (p.value === null) return null;
  const longLabel = new Intl.DateTimeFormat(undefined, {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  }).format(new Date(p.day + 'T00:00:00Z'));
  return (
    <div className="rounded-md border border-border bg-popover text-popover-foreground px-3 py-2 shadow-md">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{longLabel}</div>
      <div className="text-sm font-medium">
        {p.value}
        <span className="text-muted-foreground"> / 10</span>
      </div>
    </div>
  );
}

export function PulseSparkline({ pulses, days = 14 }: Props) {
  const series = useMemo(() => buildSeries(pulses, days), [pulses, days]);

  const real = series.filter((p) => p.value !== null) as Array<Point & { value: number }>;
  const avg14 = average(real.map((p) => p.value));

  // Trend: compare most recent 7 days vs prior 7 days (only if both have data)
  const recent = real.filter((p) => series.slice(-7).some((s) => s.day === p.day));
  const prior = real.filter((p) => series.slice(0, Math.max(0, days - 7)).some((s) => s.day === p.day));
  const avgRecent = average(recent.map((p) => p.value));
  const avgPrior = average(prior.map((p) => p.value));
  const delta = avgRecent !== null && avgPrior !== null ? avgRecent - avgPrior : null;

  if (real.length === 0) {
    return (
      <div className="flex items-center justify-center h-32 text-sm text-muted-foreground">
        Your trend will appear after your first check-in.
      </div>
    );
  }

  const TrendIcon = delta === null ? ArrowRight : delta > 0.3 ? ArrowUpRight : delta < -0.3 ? ArrowDownRight : ArrowRight;
  const trendTone =
    delta === null
      ? 'text-muted-foreground'
      : delta > 0.3
        ? 'text-emerald-600 dark:text-emerald-400'
        : delta < -0.3
          ? 'text-amber-600 dark:text-amber-400'
          : 'text-muted-foreground';

  // X-axis ticks: first, middle, last
  const tickDays = [series[0].day, series[Math.floor(series.length / 2)].day, series[series.length - 1].day];

  return (
    <div className="space-y-3">
      {/* Stats strip */}
      <div className="grid grid-cols-3 gap-3 text-sm">
        <div>
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">14-day avg</div>
          <div className="font-heading text-lg font-semibold tabular-nums">
            {avg14 !== null ? avg14.toFixed(1) : '—'}
          </div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Trend</div>
          <div className={cn('font-heading text-lg font-semibold flex items-center gap-1 tabular-nums', trendTone)}>
            <TrendIcon className="h-4 w-4" />
            {delta === null ? '—' : `${delta > 0 ? '+' : ''}${delta.toFixed(1)}`}
          </div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Check-ins</div>
          <div className="font-heading text-lg font-semibold tabular-nums">
            {real.length}
            <span className="text-muted-foreground text-sm"> / {days}</span>
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="h-36 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={series} margin={{ top: 8, right: 12, bottom: 4, left: -16 }}>
            <defs>
              <linearGradient id="pulseFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.28} />
                <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid
              vertical={false}
              stroke="hsl(var(--border))"
              strokeDasharray="3 3"
              strokeOpacity={0.6}
            />
            <XAxis
              dataKey="day"
              ticks={tickDays}
              tickFormatter={(d) => series.find((s) => s.day === d)?.label ?? ''}
              tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              interval={0}
            />
            <YAxis
              domain={[1, 10]}
              ticks={[1, 5, 10]}
              tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              width={28}
            />
            {avg14 !== null && (
              <ReferenceLine
                y={avg14}
                stroke="hsl(var(--muted-foreground))"
                strokeDasharray="2 4"
                strokeOpacity={0.7}
                label={{
                  value: `avg ${avg14.toFixed(1)}`,
                  position: 'right',
                  fill: 'hsl(var(--muted-foreground))',
                  fontSize: 10,
                }}
              />
            )}
            <Tooltip content={<TooltipCard />} cursor={{ stroke: 'hsl(var(--border))', strokeWidth: 1 }} />
            <Area
              type="monotone"
              dataKey="value"
              stroke="none"
              fill="url(#pulseFill)"
              connectNulls={false}
              isAnimationActive={false}
            />
            <Line
              type="monotone"
              dataKey="value"
              stroke="hsl(var(--primary))"
              strokeWidth={2}
              connectNulls={false}
              dot={(props: { cx?: number; cy?: number; payload?: Point; index?: number }) => {
                const { cx, cy, payload, index } = props;
                if (cx == null || cy == null || !payload || payload.value === null) {
                  return <g key={`empty-${index ?? 0}`} />;
                }
                if (payload.isToday) {
                  return (
                    <g key={payload.day}>
                      <circle cx={cx} cy={cy} r={5} fill="hsl(var(--background))" stroke="hsl(var(--primary))" strokeWidth={2} />
                      <circle cx={cx} cy={cy} r={2.5} fill="hsl(var(--primary))" />
                    </g>
                  );
                }
                return <circle key={payload.day} cx={cx} cy={cy} r={2.5} fill="hsl(var(--primary))" />;
              }}
              activeDot={{ r: 5, fill: 'hsl(var(--primary))', stroke: 'hsl(var(--background))', strokeWidth: 2 }}
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
